import React, { ReactNode } from 'react';
import {
    Alert,
    Group,
    Loader,
    Select,
    SegmentedControl,
    Text,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconInfoCircle } from '@tabler/icons-react';

import When from '../../common/When';
import { requestSurveySelectProps } from './requestSurveyForm';
import { RequestSurveyFormValues, SelectOption } from './types';

type Props = {
    form: UseFormReturnType<RequestSurveyFormValues>;
    isZoneScope: boolean;
    scopesStillLoading: boolean;
    hasZones: boolean;
    hasStores: boolean;
    availableZones: SelectOption[];
    availableStores: SelectOption[];
    loadingZones: boolean;
    loadingStores: boolean;
    onScopeKindChange: (value: string) => void;
    onZoneChange: (value: string | null) => void;
    onStoreChange: (value: string | null) => void;
};

function ScopeInfoAlert({
    title,
    children,
}: {
    title: string;
    children: ReactNode;
}) {
    return (
        <Alert
            icon={<IconInfoCircle size="1rem" />}
            title={title}
            color="blue"
            radius="md"
        >
            {children}
        </Alert>
    );
}

function RequestSurveyScopeField({
    form,
    isZoneScope,
    scopesStillLoading,
    hasZones,
    hasStores,
    availableZones,
    availableStores,
    loadingZones,
    loadingStores,
    onScopeKindChange,
    onZoneChange,
    onStoreChange,
}: Props) {
    const zoneField = (
        <When
            condition={availableZones.length > 0}
            fallback={
                <ScopeInfoAlert title="No advertising zones found">
                    You may not have any stores assigned to a zone.
                </ScopeInfoAlert>
            }
        >
            <Select
                placeholder="Search and select zone"
                data={availableZones}
                disabled={loadingZones}
                clearable
                nothingFoundMessage="No advertising zones found. You may not have any stores assigned to a zone."
                {...requestSurveySelectProps}
                {...form.getInputProps('advertisingZoneId')}
                onChange={onZoneChange}
            />
        </When>
    );

    const storeField = (
        <When
            condition={availableStores.length > 0}
            fallback={
                <ScopeInfoAlert title="No stores found">
                    No zoneless survey-enabled stores found.
                </ScopeInfoAlert>
            }
        >
            <Select
                placeholder="Search and select store"
                data={availableStores}
                disabled={loadingStores}
                clearable
                nothingFoundMessage="No zoneless survey-enabled stores found."
                {...requestSurveySelectProps}
                {...form.getInputProps('storeId')}
                onChange={onStoreChange}
            />
        </When>
    );

    return (
        <>
            <Text size="16px" mb={4}>
                Scope
            </Text>
            <SegmentedControl
                fullWidth
                color="primary"
                value={form.values.scopeKind}
                onChange={onScopeKindChange}
                data={[
                    { label: 'Advertising Zone', value: 'ZONE' },
                    { label: 'Store', value: 'STORE' },
                ]}
                classNames={{
                    root: 'mb-3',
                }}
            />
            <When
                condition={!scopesStillLoading}
                fallback={
                    <Group gap={8}>
                        <Loader size="sm" />
                        <Text size="sm" c="dimmed">
                            Loading available scopes...
                        </Text>
                    </Group>
                }
            >
                <When
                    condition={hasZones || hasStores}
                    fallback={
                        <ScopeInfoAlert title="No scopes available">
                            No advertising zones found, and no zoneless
                            survey-enabled stores are available. You may not
                            have any stores assigned to a zone.
                        </ScopeInfoAlert>
                    }
                >
                    <When condition={isZoneScope} fallback={storeField}>
                        {zoneField}
                    </When>
                </When>
            </When>
        </>
    );
}

export default RequestSurveyScopeField;
